import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import {
  api,
  parseApiError,
  type MapVetoState,
  type VetoGuide,
  type MatchCardStatus,
} from '../lib/api';
import { cn } from '../lib/cn';
import { clanLabel, MODE_LABEL } from '../lib/matchCardLabels';
import { VetoCardDownload } from './VetoCardDownload';

// ─────────────────────────────────────────────────────────────────────────────
// VetoBoard — the map ban/pick UI, extracted verbatim from MatchCardLive.tsx's
// inline VetoStrip (Lisa, 2026-07-24) so it can be reused by the standalone
// /veto/:matchId page (later thread). Pure extraction: props, logic, and JSX
// unchanged from the original.
// ─────────────────────────────────────────────────────────────────────────────

export function VetoBoard({ veto, guide, myClanId, clan1Id, clan2Id, clan1Label, clan2Label, gameModes, status, matchId, compId, canAct, isAdmin, compName, cardBranding, showDownload = false }: {
  veto?: MapVetoState; guide?: VetoGuide; myClanId?: string;
  clan1Id?: string; clan2Id?: string; clan1Label?: string; clan2Label?: string;
  gameModes: string[]; status: MatchCardStatus;
  matchId: string; compId: string; canAct: boolean; isAdmin: boolean;
  compName?: string;
  cardBranding?: { logoUrl?: string; color?: string; brand?: string };
  // The downloadable co-branded card lives on the dedicated veto PAGE only
  // (Lisa, 2026-07-24) — the match card pulls the final maps through read-only
  // above and does NOT offer the download. Defaults off so the card omits it.
  showDownload?: boolean;
}) {
  const queryClient = useQueryClient();
  const [vetoError, setVetoError] = useState('');
  const lockMutation = useMutation({
    mutationFn: () => api.matchCardVetoLock(matchId, compId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }),
  });
  const moveMutation = useMutation({
    mutationFn: (mapName: string) => api.matchCardVeto(matchId, {
      compId, action: guide!.action!, mapName, mode: guide!.mode!,
      ...(guide!.gameNum !== undefined ? { gameNum: guide!.gameNum } : {}),
    }),
    onSuccess: () => { setVetoError(''); queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }); },
    onError: (e: Error) => setVetoError(parseApiError(e).message),
  });

  // Faction gate (Lisa, 2026-07-24) — the veto MOVE endpoint 409s until a
  // faction is chosen, so this must resolve before the ban/pick board below
  // is even shown. Whoever opens the veto first (canAct) picks their side;
  // the other side auto-inherits the opposite server-side.
  const [factionError, setFactionError] = useState('');
  const factionMutation = useMutation({
    mutationFn: (faction: 'JSOC' | 'GUILD') => api.matchCardVetoFaction(matchId, { compId, faction }),
    onSuccess: () => { setFactionError(''); queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }); },
    onError: (e: Error) => setFactionError(parseApiError(e).message),
  });

  const picks = veto?.picks ?? [];
  const locked = veto?.status === 'LOCKED';
  // The guided turn panel: live while the card is in its veto window and the
  // CDL sequence still has steps. Captains act on their turn; admins can
  // drive a stalled veto (the server holds them to the same sequence).
  const vetoLive = !locked && (status === 'VETO' || status === 'SCHEDULED');
  const factionSet = !!veto?.factions;
  const turnActive = vetoLive && guide && !guide.complete;
  const itsMe = turnActive && myClanId !== undefined && guide.actorClanId === myClanId;
  const mayAct = turnActive && (itsMe || isAdmin);

  // Optional-by-design (Lisa, 2026-07-13): the veto is a courtesy step, never
  // a gate — matchLifecycle force-locks with auto-picked maps at T-10min if
  // the leaders skip it. Collapse the panel until someone actually engages so
  // the card reads as "essentials required, veto if you want it". Auto-expand
  // the moment activity exists (a ban/pick lands or the veto locks).
  const hasActivity = locked || picks.length > 0 || (veto?.bans?.length ?? 0) > 0;
  const [expanded, setExpanded] = useState(hasActivity);
  useEffect(() => { if (hasActivity) setExpanded(true); }, [hasActivity]);

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200"
          title={expanded ? 'Collapse the veto panel' : 'Expand the veto panel'}
        >
          {expanded ? <ChevronDown size={14} className="text-iron-500" /> : <ChevronRight size={14} className="text-iron-500" />}
          <ShieldCheck size={16} className={locked ? 'text-emerald-400' : 'text-amber-400'} /> Map Veto
          {!locked && (
            <span className="rounded-md bg-iron-800 px-2 py-0.5 font-mono text-[11px] text-iron-300">OPTIONAL</span>
          )}
          <span className={cn(
            'rounded-md px-2 py-0.5 font-mono text-[11px]',
            locked ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-300',
          )}>
            {locked ? 'LOCKED' : status === 'VETO' || status === 'SCHEDULED' ? 'OPEN' : 'PENDING'}
          </span>
        </button>
        {isAdmin && !locked && (
          <button
            onClick={() => lockMutation.mutate()}
            disabled={lockMutation.isPending}
            className="rounded-md border border-amber-600/60 bg-amber-900/40 px-3 py-1 font-mono text-xs text-amber-200 hover:bg-amber-900/60 disabled:opacity-50"
          >
            <Lock size={12} className="mr-1 inline" /> Lock veto
          </button>
        )}
      </div>

      {!locked && (
        <p className="mt-1 font-mono text-[11px] text-iron-500">
          Optional — leaders can ban &amp; pick maps together before the match. Skipped? Maps auto-pick 10 minutes before start.
        </p>
      )}

      {expanded && (<>
      {/* ── Faction gate (Lisa, 2026-07-24) ─────────────────────────────
          The veto MOVE endpoint 409s until factions are set, so the
          ban/pick board below is unusable — and unshown — until then. */}
      {vetoLive && !factionSet ? (
        <div className="mt-3 rounded-lg border border-iron-700 bg-iron-950 px-4 py-4">
          {canAct ? (
            <>
              <p className="font-mono text-xs text-iron-300">Choose your side to start the veto.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => factionMutation.mutate('JSOC')}
                  disabled={factionMutation.isPending}
                  className="rounded-md border border-amber-600/60 bg-amber-950/40 px-4 py-1.5 font-display text-sm font-bold uppercase tracking-wider text-amber-200 transition-colors hover:border-amber-500 hover:bg-amber-900/50 disabled:opacity-40"
                >
                  JSOC
                </button>
                <button
                  onClick={() => factionMutation.mutate('GUILD')}
                  disabled={factionMutation.isPending}
                  className="rounded-md border border-amber-600/60 bg-amber-950/40 px-4 py-1.5 font-display text-sm font-bold uppercase tracking-wider text-amber-200 transition-colors hover:border-amber-500 hover:bg-amber-900/50 disabled:opacity-40"
                >
                  GUILD
                </button>
              </div>
              {factionError && <p className="mt-2 font-mono text-[11px] text-red-400">{factionError}</p>}
            </>
          ) : (
            <p className="font-mono text-xs text-iron-500">Waiting for a captain to choose a faction to start the veto.</p>
          )}
        </div>
      ) : (<>
      {factionSet && veto?.factions && (clan1Label || clan2Label) && (
        <div className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase text-iron-400">
          <span>{clan1Label}</span>
          <span className="rounded bg-iron-800 px-1.5 py-0.5 text-amber-300">{veto.factions.clan1}</span>
          <span className="text-iron-600">vs</span>
          <span>{clan2Label}</span>
          <span className="rounded bg-iron-800 px-1.5 py-0.5 text-amber-300">{veto.factions.clan2}</span>
        </div>
      )}
      <div className="mt-3 grid gap-2 md:grid-cols-3 lg:grid-cols-5">
        {gameModes.map((mode, idx) => {
          const pick = picks.find(p => p.gameNum === idx + 1);
          return (
            <div key={`${mode}-${idx}`} className="rounded-lg border border-iron-700 bg-iron-950 px-3 py-2">
              <div className="font-mono text-[11px] uppercase text-iron-500">G{idx + 1} · {MODE_LABEL(mode)}</div>
              <div className="mt-1 font-display text-base font-bold uppercase tracking-wider text-white">
                {pick?.mapName ?? <span className="text-iron-500">— pending —</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Downloadable co-branded veto card — only once the maps are LOCKED
          (Lisa, 2026-07-23). Portal-native renderer; the site veto is
          untouched. Branding (org logo + accent) arrives on the card response,
          resolved server-side from the comp's org. */}
      {locked && showDownload && (
        <div className="mt-4 border-t border-iron-800 pt-4">
          <VetoCardDownload
            clan1Label={clan1Label ?? ''}
            clan2Label={clan2Label ?? ''}
            compName={compName}
            gameModes={gameModes}
            picks={picks.map(p => ({ gameNum: p.gameNum, mode: p.mode, mapName: p.mapName }))}
            branding={cardBranding}
            factions={veto?.factions}
          />
        </div>
      )}

      {(veto?.bans?.length ?? 0) > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          <span className="font-mono text-[11px] uppercase text-iron-500">Bans:</span>
          {veto!.bans.map((b, i) => (
            <span key={i} className="rounded bg-red-900/30 px-2 py-0.5 font-mono text-[11px] text-red-300">
              {b.mapName} <span className="text-iron-500">({MODE_LABEL(b.mode)})</span>
            </span>
          ))}
        </div>
      )}

      {/* ── Guided CDL turn panel (T12) ─────────────────────────────────── */}
      {turnActive && (
        <div className={cn(
          'mt-4 rounded-lg border px-4 py-3',
          mayAct ? 'border-amber-500/50 bg-amber-950/30' : 'border-iron-700 bg-iron-950',
        )}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-mono text-xs text-iron-300">
              <span className="text-iron-500">Step {guide!.stepIndex + 1}/{guide!.totalSteps} · </span>
              <span className="font-bold text-white">{clanLabel(guide!.actorClanId, clan1Id ?? '', clan2Id ?? '', clan1Label, clan2Label)}</span>
              {veto?.factions && (
                <span className="ml-1 rounded bg-iron-800 px-1.5 py-0.5 font-mono text-[10px] normal-case text-amber-300">
                  {guide!.actorClanId === clan1Id ? veto.factions.clan1 : veto.factions.clan2}
                </span>
              )}
              {' '}to <span className={guide!.action === 'BAN' ? 'font-bold text-red-300' : 'font-bold text-emerald-300'}>{guide!.action}</span>
              {' '}a {MODE_LABEL(guide!.mode ?? '')} map
              {guide!.gameNum !== undefined && <span className="text-iron-400"> → Game {guide!.gameNum}</span>}
            </p>
            {!mayAct && <span className="font-mono text-[11px] uppercase text-iron-500">waiting on their captain…</span>}
            {mayAct && !itsMe && isAdmin && <span className="font-mono text-[11px] uppercase text-amber-400">acting as admin</span>}
          </div>
          {mayAct && (
            <div className="mt-3 flex flex-wrap gap-2">
              {(guide!.pool ?? []).map(map => (
                <button
                  key={map}
                  onClick={() => moveMutation.mutate(map)}
                  disabled={moveMutation.isPending}
                  className={cn(
                    'rounded-md border px-3 py-1.5 font-display text-sm font-bold uppercase tracking-wider transition-colors disabled:opacity-40',
                    guide!.action === 'BAN'
                      ? 'border-red-700/60 bg-red-950/40 text-red-200 hover:border-red-500 hover:bg-red-900/50'
                      : 'border-emerald-700/60 bg-emerald-950/40 text-emerald-200 hover:border-emerald-500 hover:bg-emerald-900/50',
                  )}
                >
                  {map}
                </button>
              ))}
            </div>
          )}
          {vetoError && <p className="mt-2 font-mono text-[11px] text-red-400">{vetoError}</p>}
        </div>
      )}
      </>)}
      </>)}
    </section>
  );
}
